<script setup lang="ts">
import type { ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import { defineShortcuts } from '@nuxt/ui/composables';
import { reactive, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNil } from '#layers/base/public';
import { parseRawValue, serializeRawValue, validateRawValue } from '#layers/data-model/public';

const {
	primitiveField,
	value,
} = defineProps<{
	value: unknown;
	primitiveField?: ResolvedMonospacePrimitiveField;
	disabled?: boolean;
}>();

const emit = defineEmits<{
	close: [result: { value: unknown } | false];
}>();

const formId = useId();
const { t } = useI18n();

// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- seeds the dialog form; edits must not be overwritten
const state = reactive({
	// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- seeds the dialog form; edits must not be overwritten
	value: primitiveField?.type ? serializeRawValue(value, primitiveField.type) : String(value),
});

defineShortcuts({
	meta_enter: {
		usingInput: true,
		handler: saveEdit,
	},
});

function validateEditForm() {
	const error = primitiveField?.type ? validateRawValue(state.value, primitiveField?.type) : false;

	if (error) {
		return [{
			name: 'value',
			message: t(error),
		}];
	}

	return [];
}

function saveEdit() {
	try {
		emit('close', { value: primitiveField ? parseRawValue(state.value, primitiveField.type) : state.value });
	}
	catch (error) {
		console.error(error);
		// Validation should have caught this, but just in case
	}
}
</script>

<template>
	<DDialog
		:title="$t('common.edit_raw_value')"
		:confirm="{
			form: formId,
			label: $t('common.save'),
			type: 'submit',
			onClick: saveEdit,
			disabled,
		}"
		@close="emit('close', $event as false)"
	>
		<template #body>
			<DForm
				:id="formId"
				:state
				:validate="validateEditForm"
				:validate-on="['input']"
				@submit="saveEdit"
			>
				<DFormField name="value" :label="$t('common.raw_value')" hide-label>
					<DTextarea
						v-model="state.value"
						:placeholder="isNil(value) ? 'NULL' : ''"
						:rows="3"
						:disabled
						autofocus
						class="font-mono"
					/>
				</DFormField>
			</DForm>
		</template>
	</DDialog>
</template>
